<template>
    <wrapper-features-templates-one-column
    hideClose
    showFooter
    fullWhiteBox
    nonAuthenticatedFeature
    :customBreadcrumbs="customBreadcrumbs"
    :footerButtonsParams="footerButtonsParams.filter(el => !el.hidden)"
    :buttonFullWidth="refusedSession">
        <!-- Insert content -->
        <transition name="common-enter-animation" mode="out-in" appear>
            <div ref="elementRef" v-if="!timeExpired && !refusedSession" class="content-wrapper">
                <div
                :key="`channel${getOutOfBandValidationType}`"
                class="top-message">{{ t('initialActions.validateSession.topMessage', { channel: channel }) }}</div>

                <div :key="resetAnimation" class="timer-wrapper">
                    <svg viewBox="-50 -50 100 100" stroke-width="9">
                        <circle :r="45"></circle>
                        <circle :r="45" stroke-dasharray="282.7433388230814" stroke-dashoffset="282.7433388230814"></circle>
                    </svg>

                    <div class="timer">{{ time }}</div>
                </div>

                <div
                :key="`channel${getOutOfBandValidationType}`"
                class="bottom-message">{{ t('initialActions.validateSession.bottomMessage', { channel: channel, time: 10}) }}</div>
                <button-link
                internalClick
                :label="t('initialActions.validateSession.requestValidation')"
                :disabled="time > 20"
                @clicked="requestSessionValidation"/>
            </div>
            <div v-else-if="timeExpired && !refusedSession" class="time-expired-wrapper">
                <span class="message">{{ t('initialActions.validateSession.expiredSessionFirstMessage') }}</span>
                <span class="message">{{ t('initialActions.validateSession.expiredSessionSecondMessage', { channel: firstCharUppercase(channel) }) }}</span>
            </div>
            <div v-else-if="refusedSession" class="refused-session-wrapper">
                <span class="message">{{ t('initialActions.validateSession.refusedSessionFirstMessage') }}</span>
            </div>
        </transition>
    </wrapper-features-templates-one-column>
	<!-- Feedback toast -->
	<toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
    <!-- Generic Loader -->
	<loader-generic
	v-if="showOverlayLoader"
	isOverlay/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import ButtonLink from '@/components/generic/buttons/Link'
import LoaderGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* vue */
import { ref, computed, onMounted } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* Helpers */
import { firstCharUppercase } from '@/assets/scripts/strings'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { navigateByPath } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { getOutofBandValidationRequestStatus, retryOutofbandValidationRequest, changeAccountGroupOfSession, forceLogout, updateEntityRememberMeInfo } = mapActions(moduleMapper.SESSION)
const { submenuFetch, updateMenu } = mapActions(moduleMapper.MENU)
const { updateShowAlertRememberMeModal } = mapActions()
const { getAllCustomerInfo, getOutOfBandValidationType, getSavedEntityTillOutOfBandValidation } = mapGetters(moduleMapper.SESSION)
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['updateLoginLoader'])

emit('updateLoginLoader', false)

/* control variables */
const showOverlayLoader = ref(false)
const refusedSession = ref(false)
const timeExpired = ref(false)
const time = ref(getAllCustomerInfo.value.outOfBandSessionAuthenticationConfig.authSessionTimeout)
const elementRef = ref('')

const channel = computed(() => {
    console.log(getOutOfBandValidationType.value)
    const type = getOutOfBandValidationType.value
    return type !== 'EMAIL' ? t('initialActions.validateSession.channelUSSD') : t('initialActions.validateSession.channelEmail')
})

const customBreadcrumbs = computed(() => {
    if (refusedSession.value) return { currentArea: t('initialActions.validateSession.breadcrumbRefusedSession') }
    if (timeExpired.value) return { currentArea: t('initialActions.validateSession.breadcrumbExpiredSession') }

    return { currentArea: t('initialActions.validateSession.breadcrumb') }
})

const footerButtonsParams = computed(() => {
    return [{
        label: t('initialActions.validateSession.backToLogin'),
        secondVariant: true,
        hidden: false,
        callbackFunction: () => forceLogout()
    }, {
        label: t('initialActions.validateSession.alreadyValidated'),
        icon: 'check-line',
        buttonType: 'primary',
        iconPosition: 'right',
        hidden: refusedSession.value || timeExpired.value,
        callbackFunction: () => validateSessionStatus()
    }, {
        label: t('initialActions.validateSession.newRequest'),
        icon: 'arrow-right-full',
        buttonType: 'primary',
        iconPosition: 'right',
        hidden: !timeExpired.value,
        callbackFunction: () => requestSessionValidation()
    }]
})

const proceedToEnterApp = async () => {
    const currentDefaultAccountGroup = getAllCustomerInfo.value.accountGroups.find(obj => obj.default)
    const fakeSelectedTab = getAllCustomerInfo.value.isCompany ? 'company' : 'particular'
    let res

    try {
        /* Checking if info provided by mdw on login matches the selected entry tab user has selected */
        if (currentDefaultAccountGroup && currentDefaultAccountGroup.type.toLowerCase() !== fakeSelectedTab) {
            showOverlayLoader.value = true
            /*
            * If not and selected tab was particular,
            * We will need to get the first particular accountGroup and change the session for it
            */
            if (fakeSelectedTab === 'particular' && getAllCustomerInfo.value.accountGroups.find(obj => obj.type === 'PARTICULAR')) {
                const particularAccountGroup = getAllCustomerInfo.value.accountGroups.find(obj => obj.type === 'PARTICULAR')
                res = await changeAccountGroupOfSession({
                    data: { groupId: particularAccountGroup.id, returnMenu: true },
                    accountGroupType: particularAccountGroup.type
                })
            /*
            * If not and selected tab was company,
            * We will need to get check if initial action for picking account group is received, if not
            * the first company accountGroup and change the session for it
            */
            } else if (fakeSelectedTab === 'company' &&
                        getAllCustomerInfo.value.accountGroups.find(obj => obj.type === 'COMPANY') &&
                        !getAllCustomerInfo.value.initialActions.find(obj => obj.type === 'CHOOSE_DEFAULT_CONTRACT_ACCOUNT_GROUP')) {
                const companyAccountGroup = getAllCustomerInfo.value.accountGroups.find(obj => obj.type === 'COMPANY')
                res = await changeAccountGroupOfSession({
                    data: { groupId: companyAccountGroup.id, returnMenu: true },
                    accountGroupType: companyAccountGroup.type
                })
            }
        }
        /* If we have info waiting for sucess validation before saving it in our remember saved info */
        if (getSavedEntityTillOutOfBandValidation.value) {
            const parsedData = getSavedEntityTillOutOfBandValidation.value
            delete parsedData.requiresOutOfBandSessionAuthentication
            const saveEntityResult = await updateEntityRememberMeInfo(parsedData)

			if (saveEntityResult.errorCode === 'entity_not_match_saved') updateShowAlertRememberMeModal(saveEntityResult.errorCode === 'entity_not_match_saved')
        }
        if (res) {
            await updateMenu(res.data.data.menuItemList)
            if (getMobileAppState.value) await submenuFetch()
        }
        !getMobileAppState.value ? navigateByPath(routeList.HOME_PATH) : navigateByPath(routeList.HOME_MOBILE_PATH)
    } catch (error) {
		errorHandler(error.response?.data)
    }
}

const interval = ref(null)
const startTimer = () => {
	if (interval.value) clearInterval(interval.value)

	interval.value = setInterval(() => {
		if (time.value <= 1) {
			clearInterval(interval.value)
			time.value = 0
            timeExpired.value = true
		} else {
			time.value--
		}
	}, 1000)
}

const validationInterval = ref(null)
const startValidationLoop = () => {
	if (validationInterval.value) clearInterval(validationInterval.value)

	validationInterval.value = setInterval(() => {
		if (time.value <= 1) {
			clearInterval(validationInterval.value)
		} else {
			validateSessionStatus()
		}
	}, 4000)
}

const validateSessionStatus = async () => {
	try {
		const response = await getOutofBandValidationRequestStatus()
		if (response.data.data.status === 'VALID') {
			clearInterval(interval.value)
			clearInterval(validationInterval.value)
            proceedToEnterApp()
		} else if (response.data.data.status === 'INVALID') {
            clearInterval(interval.value)
			clearInterval(validationInterval.value)
            refusedSession.value = true
        }
	} catch (error) {
		clearInterval(interval.value)
        clearInterval(validationInterval.value)
        refusedSession.value = true
	}
}

const stopTimer = () => {
	clearInterval(interval.value)
}

const resetAnimation = ref(false)
const requestSessionValidation = async () => {
	showOverlayLoader.value = true

	stopTimer()
	time.value = getAllCustomerInfo.value.outOfBandSessionAuthenticationConfig.authSessionTimeout
	try {
		await retryOutofbandValidationRequest()
        resetAnimation.value = !resetAnimation.value
        timeExpired.value = false
		startTimer()
		startValidationLoop()
	} catch (error) {
	} finally {
        showOverlayLoader.value = false
    }
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor({ ...data, dismissToastBtnText: t('login.errorFeedbackToast') }, retryFn)
	showFeedbackToast.value = true
    showOverlayLoader.value = false
}

startTimer()
startValidationLoop()

onMounted(() => {
    const element = elementRef.value
    element.style.setProperty('--animationTime', getAllCustomerInfo.value.outOfBandSessionAuthenticationConfig.authSessionTimeout + 's')
})
</script>

<style lang="scss" scoped>
.content-wrapper {
	@include commonEnteringAnimation;

    width: 508px;
    height: 100%;

    display: flex;
    flex-direction: column;
    align-items: center;
    align-self: center;
    justify-content: center;

    gap: 32px;

    --animationTime: 30s;

    .top-message {
        width: 100%;

        @include H6-Regular;
        line-height: 18px;
        color: var(--secondary-dark-3);
    }

    .timer-wrapper {
        position: relative;
        svg {
            width: 160px;
            height: 160px;
            overflow: visible;

			transform: scale(-1,1);

            :deep(circle) {
                fill: none;

                &:not(:last-of-type) {
                    stroke: var(--secondary-light-1);
                }
            }

            :deep(circle + circle) {
				transform: rotate(-90deg);
                stroke: var(--primary);
                animation-name: arc;
                animation: arc var(--animationTime) linear;
            }
        }

        .timer {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);

            @include H1-SemiBold;
            line-height: 45px;
            color: var(--primary);
        }
    }

    .bottom-message {
        @include H7-Regular;
        line-height: 18px;
        color: var(--secondary-dark-3);
    }
    .wrapper {
        width: 100%;

        :deep(.button-link) {
            align-self: flex-start
        }
    }
}

@keyframes arc {
    0% {
        stroke-dashoffset: 0;
    }
    100% {
        stroke-dashoffset: 282;
    }
}

.time-expired-wrapper, .refused-session-wrapper {
	@include commonEnteringAnimation;

    width: 508px;
    height: 100%;

    display: flex;
    flex-direction: column;
    align-self: center;
    justify-content: center;

    gap: 16px;

    .message {
        @include H6-Regular;
        line-height: 18px;
        color: var(--secondary-dark-3);
    }
}
</style>
